import React from 'react';
import { User, Users, Award } from 'lucide-react';
import BiographySection from './BiographySection';
import IdentityDetailSection from './IdentityDetailSection';
import { CharacterData } from '../../types/character';
import './IdentitySection.css';

interface IdentitySectionProps {
  character: CharacterData;
  heritageData?: any;
  communityData?: any;
  defaultExpanded?: boolean;
  priority?: 'high' | 'medium' | 'low';
}

/**
 * IdentitySection - Card-based display for character identity
 * Shows primary info in a prominent card with collapsible heritage/community details
 */
const IdentitySection: React.FC<IdentitySectionProps> = ({
  character,
  heritageData,
  communityData,
  defaultExpanded = true,
  priority = 'high'
}) => {
  // Check if we have basic character data
  const hasBasicInfo = character.name || character.class || character.heritage || character.community;

  // Parse heritage traits
  const heritageTraits = heritageData?.traits?.map((trait: any) => ({
    name: trait.name,
    description: trait.description
  })) || [];

  // Parse community features
  const communityFeatures = communityData?.features?.map((feature: any) => ({
    name: feature.name,
    description: feature.description
  })) || [];

  // Check if section is empty
  const isEmpty = !hasBasicInfo && !heritageData && !communityData;

  if (isEmpty) {
    return (
      <BiographySection
        sectionId="bio-identity"
        title="Character Identity"
        icon={<User size={18} />}
        defaultExpanded={defaultExpanded}
        priority={priority}
        isEmpty={true}
        emptyMessage="Complete character creation to see identity information here."
      >
        <div className="biography-empty">
          <div className="biography-empty__message">
            Complete character creation to see identity information here.
          </div>
        </div>
      </BiographySection>
    );
  }

  const level = typeof character.level === 'number' ? character.level : parseInt(character.level || '1', 10) || 1;
  const tier = typeof character.tier === 'number' ? character.tier : parseInt(character.tier || '1', 10) || 1;

  // Format class/subclass names: capitalize and replace hyphens/underscores with spaces
  const formatName = (name: string) => {
    return name
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <BiographySection
      sectionId="bio-identity"
      title="Character Identity"
      icon={<User size={18} />}
      defaultExpanded={defaultExpanded}
      priority={priority}
      isEmpty={isEmpty}
      emptyMessage="Character identity information not available"
    >
      {/* Character Name */}
      <div className="identity-primary-card__name-section">
        <div className="identity-primary-card__icon">
          <User size={32} />
        </div>
        <h2 className="identity-primary-card__name">
          {character.name || 'Unnamed Character'}
        </h2>
        {(character.class || character.subclass) && (
          <div className="identity-primary-card__class">
            {character.class && (
              <span className="identity-primary-card__class-name">{formatName(character.class)}</span>
            )}
            {character.class && character.subclass && (
              <span className="identity-primary-card__separator">-</span>
            )}
            {character.subclass && (
              <span className="identity-primary-card__subclass">{formatName(character.subclass)}</span>
            )}
          </div>
        )}
      </div>

      {/* Stats Pills */}
      <div className="identity-primary-card__stats">
        <div className="identity-stat-pill identity-stat-pill--level">
          <Award size={16} />
          <span className="identity-stat-pill__label">Level</span>
          <span className="identity-stat-pill__value">{level}</span>
        </div>
        <div className="identity-stat-pill identity-stat-pill--tier">
          <Award size={16} />
          <span className="identity-stat-pill__label">Tier</span>
          <span className="identity-stat-pill__value">{tier}</span>
        </div>
      </div>

      {/* Heritage & Community Detail Sections */}
      {(heritageData?.description || heritageTraits.length > 0) && (
        <IdentityDetailSection
          title={character.heritage || 'Heritage'}
          description={heritageData?.description}
          features={heritageTraits}
          variant="heritage"
          defaultExpanded={false}
        />
      )}

      {(communityData?.description || communityFeatures.length > 0) && (
        <IdentityDetailSection
          title={character.community || 'Community'}
          description={communityData?.description}
          features={communityFeatures}
          variant="community"
          defaultExpanded={false}
        />
      )}
    </BiographySection>
  );
};

export default IdentitySection;